import React, { useMemo, useState } from 'react';
import { todayISO } from '../domain/clock';
import {
  HOME_WORDING as W, agendaRows, greeting, greetingName, homeTiles, homeTilesKnown, paymentsInMonth,
} from '../domain/home';
import { reading } from '../domain/reading';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import EmptyState from '../components/EmptyState';
import GettingStarted from '../components/home/GettingStarted';
import StatTiles from '../components/home/StatTiles';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { useStudents } from '../data/useStudents';
import { useLedger } from '../data/useLedger';
import { useFamilies } from '../data/useFamilies';
import { useEvents } from '../data/useEvents';
import { useAttendance } from '../data/useAttendance';
import AgendaRow from '../components/home/AgendaRow';
import AttendanceDrawer from '../components/calendar/AttendanceDrawer';
import s from '../components/home/home.module.css';

// Home — the landing surface of a signed-in studio.
//
// The shape is the MMS zero state (SPEC.md §5): greeting, dismissible
// getting-started row, stat tiles, then today's agenda. A new studio sees a
// working screen with real (zero) numbers rather than a modal asking it to
// fill in a form first.

function CalendarArt() {
  return (
    <svg width="96" height="96" viewBox="0 0 96 96" fill="none" aria-hidden="true">
      <rect x="14" y="22" width="68" height="58" rx="8" fill="currentColor" opacity="0.14" />
      <rect x="14" y="22" width="68" height="16" rx="8" fill="currentColor" opacity="0.35" />
      <circle cx="34" cy="18" r="4" fill="currentColor" opacity="0.5" />
      <circle cx="62" cy="18" r="4" fill="currentColor" opacity="0.5" />
      <rect x="26" y="48" width="14" height="10" rx="3" fill="currentColor" opacity="0.4" />
      <rect x="46" y="48" width="14" height="10" rx="3" fill="currentColor" opacity="0.25" />
      <rect x="26" y="63" width="14" height="10" rx="3" fill="currentColor" opacity="0.25" />
    </svg>
  );
}

// "Monday, September 1" in the studio's locale.
//
// The one date on screen that is genuinely an instant rather than a date-only
// string — it is "now", read in the reader's own zone — so it does go through
// `new Date()`, unlike everything the ledger stores.
function todayLabel(locale) {
  return new Date().toLocaleDateString(locale, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export default function HomePage() {
  const { user } = useAuth();
  const { locale } = useLocale();
  const { activeStudents, getStudent, loading: studentsLoading } = useStudents();
  // `loading`: the series listener has not answered yet. An unread schedule
  // is not an empty day, so the agenda's empty state waits for it (#626), and
  // so does the tile that counts it (#629) — `reading`, src/domain/reading.js.
  const { occurrencesOn, loading, failed: eventsFailed } = useEvents();
  const { statusFor } = useAttendance();
  const { entries, totalOwed, unreadable, loading: ledgerLoading } = useLedger();
  // The families the studio has: "You're owed" is theirs, the same figure as
  // the Families & Invoices header (#640).
  const { families = [], loading: familiesLoading } = useFamilies();
  const familyIds = useMemo(() => families.map((f) => f.id), [families]);
  const navigate = useNavigate();

  // Edit Attendance opens the same drawer the calendar's event popover opens
  // (AttendanceDrawer, src/components/calendar/AttendanceDrawer.jsx), over
  // Home rather than navigating to /calendar (#581) — a teacher between
  // lessons marking attendance from the agenda should not lose this screen.
  const [attendanceTarget, setAttendanceTarget] = useState(null);

  const firstName = useMemo(() => greetingName(user?.displayName, user?.email), [user]);

  const today = todayISO();

  // Today's lessons, expanded live from the series. Each attendee is its own
  // row so attendance can be taken from Home without opening the calendar —
  // which is how MMS works, and how a teacher between lessons actually wants it.
  const agenda = useMemo(
    // A removed lesson is not produced by its rule and never arrives here
    // (#152); a lesson called off is the `Teacher Cancelled` attendance
    // status, and it belongs on this list: it is on the calendar, the row shows
    // its status, and this is a place a teacher can change it.
    //
    // The rows are `agendaRows` (src/domain/home.js): one per student on each
    // lesson, one with no student for a lesson nobody is on.
    () => {
      const occurrences = occurrencesOn(today);
      const byId = new Map(occurrences.map((o) => [o.id, o]));
      return agendaRows(occurrences, statusFor).map((row) => ({
        key: row.key,
        occurrence: byId.get(row.occurrenceId),
        studentId: row.studentId,
        student: row.studentId ? getStudent(row.studentId) : null,
        status: row.status,
      }));
    },
    [occurrencesOn, today, getStudent, statusFor],
  );

  const tiles = useMemo(() => {
    // `paymentsInMonth` and `homeTiles` (src/domain/home.js); isActive rather
    // than a hand-rolled !voidedAt — see ledger.js and issue #70.
    //
    // "This month" is the month of `today`, the agenda's own day on the
    // teacher's wall clock, not UTC's.
    //
    // Each tile shows the dash, not a zero, until the reads it is folded from
    // have answered (#629).
    const known = homeTilesKnown({
      series: !loading, students: !studentsLoading, ledger: !ledgerLoading, families: !familiesLoading,
    });
    return homeTiles({
      lessonsToday: agenda.length,
      activeStudents: activeStudents.length,
      owedMinor: totalOwed(familyIds),
      unreadable: unreadable().length,
      paymentsMinor: paymentsInMonth(entries, today),
    }).map((t, i) => ({ ...t, known: known[i] }));
  }, [activeStudents, entries, totalOwed, familyIds, unreadable, agenda.length, today, loading, studentsLoading, ledgerLoading, familiesLoading]);

  return (
    <AppShell title={W.title}>

      <div className={s.home}>
        <div className={s.greeting}>{greeting(firstName)}</div>

        <GettingStarted />

        <StatTiles tiles={tiles} />

        <section className={s.section}>
          <div className={s.sectionHead}>
            <span className={s.sectionDate}>{todayLabel(locale)}</span>
            <span className={s.sectionTitle}>{W.agenda}</span>
          </div>
          {agenda.length === 0 ? (
            <EmptyState
              reading={reading(!loading, agenda.length, eventsFailed)}
              illustration={<CalendarArt />}
              title={W.emptyTitle}
              description={W.emptyDescription}
              actionLabel={W.emptyAction}
              onAction={() => navigate('/calendar')}
            />
          ) : (
            <div>
              {agenda.map((row) => (
                <AgendaRow
                  key={row.key}
                  row={row}
                  onEditAttendance={() => setAttendanceTarget({
                    occurrence: row.occurrence,
                    studentId: row.studentId,
                  })}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <AttendanceDrawer
        open={Boolean(attendanceTarget)}
        onClose={() => setAttendanceTarget(null)}
        occurrence={attendanceTarget?.occurrence}
        studentId={attendanceTarget?.studentId}
      />
    </AppShell>
  );
}
